import { useRef, useCallback, useEffect } from "react";

/**
 * @param {function(string, string, function): function} createSubscription
 *   (recordId, userId, onUpdated) => unsubscribe
 */
export function useManagedRealtimeSubscriptions(createSubscription) {
  const activeRef = useRef(new Map());

  const stop = useCallback((recordId) => {
    if (!recordId) return;

    const unsubscribe = activeRef.current.get(recordId);
    if (!unsubscribe) return;

    activeRef.current.delete(recordId);
    unsubscribe();
  }, []);

  const start = useCallback(
    (recordId, onUpdated, userId) => {
      if (!recordId || !userId) return;
      if (activeRef.current.has(recordId)) return;

      const unsubscribe = createSubscription(recordId, userId, onUpdated);
      activeRef.current.set(recordId, unsubscribe);
    },
    [createSubscription]
  );

  const stopAll = useCallback(() => {
    activeRef.current.forEach((unsubscribe) => unsubscribe());
    activeRef.current.clear();
  }, []);

  useEffect(
    () => () => {
      activeRef.current.forEach((unsubscribe) => unsubscribe());
      activeRef.current.clear();
    },
    []
  );

  return { start, stop, stopAll, activeRef };
}
